import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/i18n/LanguageContext';
import ContentEditor from '@/modules/lcms/content-courses/components/content-editor';
import { courseContentService } from '@/modules/lcms/content-courses/services/courseContentService';
import { ContentCourse } from '@/modules/lcms/content-courses/schemas/schemas';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

interface FormErrors {
  title?: string;
  subject?: string;
  level?: string;
}

const NewCourse: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  const [formData, setFormData] = useState({
    title: '',
    subject: '',
    level: '',
    description: '',
    content: '',
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  
  const validateForm = (): boolean => {
    const errors: FormErrors = {};
    let isValid = true;
    
    if (!formData.title.trim()) {
      errors.title = 'Course title is required';
      isValid = false;
    } else if (formData.title.trim().length < 3) {
      errors.title = 'Course title must be at least 3 characters';
      isValid = false;
    }
    
    if (!formData.subject) {
      errors.subject = 'Subject is required';
      isValid = false;
    }
    
    if (!formData.level) {
      errors.level = 'Grade level is required';
      isValid = false;
    }
    
    setFormErrors(errors);
    return isValid;
  };
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field if it exists
    if (formErrors[name as keyof FormErrors]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
    
    // Clear general error when form changes
    if (error) {
      setError(null);
    }
  };

  const handleContentChange = (html: string) => {
    setFormData(prev => ({
      ...prev,
      content: html
    }));
    
    // Clear general error when content changes
    if (error) {
      setError(null);
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      window.scrollTo(0, 0); // Scroll to top to show errors
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      const timestamp = new Date().toISOString();
      
      // Convert form data to ContentCourse format
      const courseData: Partial<ContentCourse> = {
        name: formData.title.trim(),
        title: formData.title.trim(),
        subject: formData.subject,
        grade: formData.level,
        level: formData.level,
        description: formData.description.trim(),
        content: formData.content,
        status: 'draft',
        createdAt: timestamp,
        updatedAt: timestamp,
      };
      
      // Create course using the service
      const createdCourse = await courseContentService.createCourse(courseData);
      
      if (!createdCourse || !createdCourse.id) {
        throw new Error('Failed to create course: Invalid response from server');
      }
      
      // Navigate to the newly created course
      navigate(`/lcms/course-content/${createdCourse.id}`);
    } catch (error) {
      console.error('Error creating course:', error);
      setError(error instanceof Error ? error.message : 'Failed to create course. Please try again.');
      window.scrollTo(0, 0); // Scroll to top to show error
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleCancel = () => {
    // If form has data, confirm before navigating away
    if (formData.title || formData.subject || formData.level || formData.description || formData.content) {
      setShowLeaveConfirm(true);
      return;
    }
    navigate('/lcms/course-content');
  };

  // Chỉ rời trang khi bấm "Xác nhận" trong Dialog.
  const confirmLeave = () => {
    setShowLeaveConfirm(false);
    navigate('/lcms/course-content');
  };
  
  const subjects = ['Mathematics', 'Physics', 'Chemistry', 'Biology', 'Literature', 'History', 'Geography'];
  const levels = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];
  
  return (
    <div className="container mx-auto py-6">
      <h1 className="text-2xl font-bold mb-6">{t('course-content.createCourse')}</h1>
      
      <div className="bg-white rounded-lg shadow-md p-6">
        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 gap-6 mb-6">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                Course Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="title"
                name="title"
                required
                value={formData.title}
                onChange={handleChange}
                className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  formErrors.title ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter course title"
                disabled={isSubmitting}
              />
              {formErrors.title && (
                <p className="mt-1 text-sm text-red-500">{formErrors.title}</p>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                  Subject <span className="text-red-500">*</span>
                </label>
                <select
                  id="subject"
                  name="subject"
                  required
                  value={formData.subject}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    formErrors.subject ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={isSubmitting}
                >
                  <option value="">Select a subject</option>
                  {subjects.map(subject => (
                    <option key={subject} value={subject}>{subject}</option>
                  ))}
                </select>
                {formErrors.subject && (
                  <p className="mt-1 text-sm text-red-500">{formErrors.subject}</p>
                )}
              </div>
              
              <div>
                <label htmlFor="level" className="block text-sm font-medium text-gray-700 mb-1">
                  Grade Level <span className="text-red-500">*</span>
                </label>
                <select
                  id="level"
                  name="level"
                  required
                  value={formData.level}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    formErrors.level ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={isSubmitting}
                >
                  <option value="">Select a grade level</option>
                  {levels.map(level => (
                    <option key={level} value={level}>Grade {level}</option>
                  ))}
                </select>
                {formErrors.level && (
                  <p className="mt-1 text-sm text-red-500">{formErrors.level}</p>
                )}
              </div>
            </div>
            
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter course description"
                disabled={isSubmitting}
              ></textarea>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Course Content
              </label>
              <ContentEditor 
                initialContent={formData.content} 
                onChange={handleContentChange}
              />
            </div>
          </div>
          
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating...' : 'Create Course'}
            </button>
          </div>
        </form>
      </div>

      <Dialog open={showLeaveConfirm} onOpenChange={setShowLeaveConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Unsaved changes</DialogTitle>
            <DialogDescription>You have unsaved changes. Are you sure you want to leave?</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLeaveConfirm(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={confirmLeave}>
              Leave
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NewCourse; 